import type { CSSProperties } from "react";
import { PLATFORM_LABEL, type Connection, type Platform, type ValidationResult } from "../lib/types";
import { PLATFORM_ICON } from "./PlatformIcons";
import type { Brand } from "../lib/brands";

// The account names run in a spread of hues so neighbours in the row never read
// as the same colour and the eye can pick one account out of a dozen. Only the
// hue is chosen here — how light it has to be to stay readable is the theme's
// call (ADR-0014, `.swatch-ink` in theme.css). 67° apart is far enough that no
// two adjacent chips collide, and the cycle only repeats after five accounts.
const HUE_STEP = 67;

interface Props {
  connections: Connection[];
  selected: Set<string>;
  results: Map<string, ValidationResult>;
  onToggle: (id: string) => void;
  /** Tick or untick a whole brand at once. "Post as labtest1" is the action
   *  being taken, and doing it three chips at a time is where the wrong Page
   *  gets picked up. */
  onToggleMany: (ids: string[], on: boolean) => void;
  onFix: (platform: string, fix: string) => void;
  checking: boolean;
  /** The workspace's brands (ADR-0029). Empty is a normal state — a workspace
   *  whose brand list could not be read still publishes. */
  brands: Brand[];
  /** The brand chosen in the top bar, drawn first and named as such. */
  activeBrandId: string | null;
}

/** One drawer of accounts, as the picker draws it. */
interface Group {
  id: string | null;
  name: string;
  connections: Connection[];
}

/**
 * Accounts in brand order: the brand being viewed first, then the rest by name,
 * then anything unfiled.
 *
 * Returns a single unnamed group when there is nothing to separate — one brand,
 * or none. Grouping two accounts under one heading is a heading that earns
 * nothing, and the flat row is what every existing workspace already has.
 */
function grouped(connections: Connection[], brands: Brand[], activeBrandId: string | null): Group[] {
  const byId = new Map(brands.map((b) => [b.id, b]));
  const groups: Group[] = [];

  for (const b of brands) {
    const mine = connections.filter((c) => c.brand_id === b.id);
    if (mine.length > 0) groups.push({ id: b.id, name: b.name, connections: mine });
  }
  // A brand id pointing at a brand this reader cannot see is treated as
  // unfiled rather than dropped: an account must never vanish from the picker
  // because of how it was filed.
  const unfiled = connections.filter((c) => !c.brand_id || !byId.has(c.brand_id));
  if (unfiled.length > 0) groups.push({ id: null, name: "Unfiled", connections: unfiled });

  if (groups.length < 2) return [{ id: null, name: "", connections }];

  groups.sort((a, b) => {
    if (a.id === activeBrandId) return -1;
    if (b.id === activeBrandId) return 1;
    if (a.id === null) return 1;
    if (b.id === null) return -1;
    return a.name.localeCompare(b.name);
  });
  return groups;
}

export function TargetPicker({
  connections, selected, results, onToggle, onToggleMany, onFix, checking,
  brands, activeBrandId,
}: Props) {
  if (connections.length === 0) {
    return <p className="muted">No active accounts. Connect one first.</p>;
  }

  // Verdicts are per platform, not per account, so five Facebook pages picked at
  // once share one set of problems. Listing them once under the row beats
  // repeating the same sentence beside every chip.
  const verdictPlatforms: Platform[] = [];
  for (const c of connections) {
    if (selected.has(c.id) && results.has(c.platform) && !verdictPlatforms.includes(c.platform)) {
      verdictPlatforms.push(c.platform);
    }
  }

  const groups = grouped(connections, brands, activeBrandId);
  // The hue runs across the whole picker rather than restarting per group, so
  // two accounts never share a colour just because they are in different
  // drawers. `indexOf` on a list this size is cheaper than threading a counter.
  const hueOf = (c: Connection) => (connections.indexOf(c) * HUE_STEP) % 360;

  return (
    <>
      {groups.map((g) => {
        const ids = g.connections.map((c) => c.id);
        const allOn = ids.every((id) => selected.has(id));
        const someOn = ids.some((id) => selected.has(id));

        return (
          <div key={g.id ?? "_"} className={`target-group${g.name ? "" : " bare"}`}>
            {/* No heading when there is nothing to separate — a single-brand
                workspace gets exactly the row it had before brands existed. */}
            {g.name && (
              <div className="target-group-head">
                <strong>{g.name}</strong>
                {g.id === activeBrandId && g.id !== null && (
                  <span className="chip">Viewing</span>
                )}
                <span className="muted">
                  {ids.length} account{ids.length === 1 ? "" : "s"}
                </span>
                <span className="spacer" />
                {/* "Post as labtest1" in one press, which is the action being
                    taken every time. The label says which way it will go, so it
                    is never a guess about what a press does. */}
                <button
                  type="button"
                  className="link sm"
                  onClick={() => onToggleMany(ids, !allOn)}
                  aria-label={allOn
                    ? `Clear every account in ${g.name}`
                    : `Select every account in ${g.name}`}
                >
                  {allOn ? "Clear" : someOn ? "Select the rest" : "Select all"}
                </button>
              </div>
            )}

            <div className="target-row">
              {g.connections.map((c) => {
                const isSelected = selected.has(c.id);
                const result = results.get(c.platform);
                const Icon = PLATFORM_ICON[c.platform];

                return (
                  <label key={c.id} className={`target-chip${isSelected ? " selected" : ""}`}>
                    <input
                      type="checkbox"
                      className="visually-hidden"
                      checked={isSelected}
                      onChange={() => onToggle(c.id)}
                      aria-label={`Publish to ${c.display_name} on ${PLATFORM_LABEL[c.platform]}`}
                    />
                    <Icon />
                    <span className="name swatch-ink"
                          style={{ "--swatch-h": hueOf(c) } as CSSProperties}>
                      {c.display_name}
                    </span>
                    {isSelected && result && (
                      checking
                        ? <span className="dot busy" title="Checking…" />
                        : result.ok
                          ? <span className="dot ok" title="Ready" />
                          : <span className="dot err" title="Will be rejected" />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {verdictPlatforms.map((platform) => {
        const result = results.get(platform)!;
        const Icon = PLATFORM_ICON[platform];

        return (
          <div key={platform} className="target-verdict">
            <div className="row">
              <Icon />
              <strong>{PLATFORM_LABEL[platform]}</strong>
              {checking
                ? <span className="chip busy">Checking…</span>
                : result.ok
                  ? <span className="chip ok">Ready</span>
                  : <span className="chip err">Will be rejected</span>}
            </div>

            {result.problems.map((p, i) => (
              <div key={i} className={`problem ${p.severity}`}>
                {p.message}
                {p.fixable && (
                  <button className="link sm" onClick={() => onFix(platform, p.fixable!)}>
                    {p.fixable === "convert-jpeg" ? "Convert to JPEG" :
                     p.fixable === "resize" ? "Resize to fit" : "Compress"}
                  </button>
                )}
              </div>
            ))}

            {result.sourceUrl && !result.ok && (
              <div className="muted" style={{ marginTop: ".3rem", fontSize: ".78rem" }}>
                <a href={result.sourceUrl} target="_blank" rel="noreferrer">
                  {PLATFORM_LABEL[platform]}'s published specification
                </a>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
